/**
 * AgentProfileFacet — ADR-111 IA-1/IA-2: the per-agent Profile facet. Edits
 * model/enabled/role for the ONE agent selected on the rail (no picker — the
 * agent is already chosen), plus its routing (ADR-113) and its retirement.
 * Renders read-only for a core-owned agent (ADR-103). Every change goes out as
 * a PR + in-app improvement — nothing runs or stops until it's approved.
 */
import { useEffect, useState } from 'react'
import { api, AuthorTestsFailingError } from '../api/client'
import type { FailingCheck, ProposeSkillResult } from '../api/client'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { DeletePanel } from '../components/DeletePanel'
import { ProposeResultBanner } from '../components/ProposeResultBanner'
import { DEFAULT_MODEL, DEFAULT_PROVIDER, modelLabel } from './agentModels'
import { ModelPicker, providerOf, useModelCatalog } from '../components/ModelPicker'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

// ADR-111 item 1 / ADR-113: edit a specialist's routing: intent (+ capability
// bullets). Proposing regenerates the brain's fleet-roster server-side and
// lands both via the normal Approve & Deploy flow. Its own state/propose so it
// doesn't entangle with the model/enabled Profile form above it.
function RoutingSubForm({ agent, canPropose }: { agent: string; canPropose: boolean }) {
  const [intent, setIntent] = useState('')
  const [caps, setCaps] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [result, setResult] = useState<ProposeSkillResult | null>(null)

  const propose = async () => {
    setBusy(true); setErr(null); setFailingChecks([])
    try {
      const capabilities = caps.split('\n').map(s => s.trim()).filter(Boolean)
      const r = await api.proposeRouting(agent, {
        intent: intent.trim(),
        capabilities: capabilities.length ? capabilities : undefined,
        message: note.trim() || undefined,
      })
      setResult(r); setIntent(''); setCaps(''); setNote('')
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else setErr(em(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 px-4 py-3 space-y-3 max-w-xl">
      <div>
        <h2 className="text-sm font-semibold text-gray-800">Routing</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          How the brain decides to delegate to <span className="font-mono">{agent}</span>. Proposing regenerates the brain's routing table (ADR-113). Leave blank to keep the current routing; the brain's <span className="font-medium">Routing</span> facet shows the live result.
        </p>
      </div>
      {result && <ProposeResultBanner result={result} />}
      <label className="text-xs text-gray-600 block">Intent — the request type that should route here
        <textarea value={intent} onChange={e => setIntent(e.target.value)} rows={2}
          placeholder="e.g. GA4 metrics, dashboards, and traffic analysis"
          className="mt-1 block w-full text-sm border border-gray-300 rounded px-2 py-1" />
      </label>
      <label className="text-xs text-gray-600 block">Capabilities — one per line (optional)
        <textarea value={caps} onChange={e => setCaps(e.target.value)} rows={3}
          placeholder={'GA4 Data API queries\ntrend & anomaly analysis'}
          className="mt-1 block w-full text-sm border border-gray-300 rounded px-2 py-1 font-mono" />
      </label>
      {err && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{err}</p>}
      {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}
      <div className="flex items-center gap-2">
        {canPropose ? (
          <button type="button" disabled={busy || intent.trim() === ''} onClick={() => void propose()}
            className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
            {busy ? 'Proposing…' : 'Propose routing'}
          </button>
        ) : <span className="text-xs text-gray-500">Only a fleet owner can propose routing.</span>}
        <input type="text" value={note} onChange={e => setNote(e.target.value)}
          placeholder="Optional note for the reviewer" className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
      </div>
    </div>
  )
}

/** AgentProfileFacet — the per-agent Profile facet: model/enabled/role, edited
 * for the ONE agent selected on the rail. On mount it loads the agent's
 * CURRENT model/enabled via GET /api/agents/:agent (GET /api/agents itself
 * stays names-only — two pickers depend on that exact shape) and pre-selects
 * the form against those values, so "Propose changes" only sends fields that
 * actually differ from what's live in config/fleet.yaml today. */
export function AgentProfileFacet({
  agent,
  canPropose,
  isCoreOwned,
}: {
  agent: string
  canPropose: boolean
  isCoreOwned: boolean
}) {
  // The agent's CURRENT values, as loaded from GET /api/agents/:agent — the
  // baseline "Propose changes" diffs the form against. currentModel is the
  // RAW explicit model.default ('' when the profile inherits the fleet
  // default); currentModelExplicit distinguishes "unset" from "explicitly
  // set to the same value as the default."
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [currentModel, setCurrentModel] = useState('')
  const [currentModelExplicit, setCurrentModelExplicit] = useState(false)
  const [currentEnabled, setCurrentEnabled] = useState(true)

  // The form's live selections, pre-seeded from the loaded current values
  // once they arrive.
  const [model, setModel] = useState(DEFAULT_MODEL)
  // ADR-182 D3: a choice is a (provider, model) pair, so the form tracks both.
  const [provider, setProvider] = useState(DEFAULT_PROVIDER)
  const [enabled, setEnabled] = useState(true)
  const [role, setRole] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [result, setResult] = useState<ProposeSkillResult | null>(null)
  const { catalog, catalogError, catalogLoading } = useModelCatalog()

  useEffect(() => {
    // Runs for a core agent too now (ADR-184 D2): its model form needs the
    // current value to baseline against, exactly like a fleet agent's.
    let cancelled = false
    setLoading(true); setLoadErr(null)
    api.getAgentProfile(agent)
      .then(p => {
        if (cancelled) return
        setCurrentModel(p.model)
        setCurrentModelExplicit(p.model_is_explicit)
        setCurrentEnabled(p.enabled)
        setModel(p.model_is_explicit ? p.model : DEFAULT_MODEL)
        setEnabled(p.enabled)
      })
      .catch(e => { if (!cancelled) setLoadErr(em(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [agent])

  // The effective current model: the explicit pin, or the fleet default when
  // the profile doesn't set one. What the Model select compares itself
  // against to decide whether the operator actually changed anything.
  const effectiveCurrentModel = currentModelExplicit ? currentModel : DEFAULT_MODEL

  // The provider that currently serves it, resolved from the catalog. The
  // profile's own model.provider is not returned by GET /api/agents/:agent
  // today, so this is derived — and derived is honest here, because the catalog
  // is the same source the write path validates against.
  const effectiveCurrentProvider =
    providerOf(catalog, effectiveCurrentModel) ?? DEFAULT_PROVIDER

  // Seed the picker from the current pair once the catalog has loaded.
  useEffect(() => {
    if (!catalog) return
    setProvider(providerOf(catalog, effectiveCurrentModel) ?? DEFAULT_PROVIDER)
  }, [catalog, effectiveCurrentModel])

  // A pin the catalog does not list stays VISIBLE rather than being silently
  // reset — a profile running an unlisted model is a fact to surface.
  const currentUnlisted =
    currentModelExplicit && catalog && !providerOf(catalog, currentModel)
      ? currentModel
      : undefined

  const modelChanged = !loading && (model !== effectiveCurrentModel ||
    provider !== effectiveCurrentProvider)

  // ADR-184 D4: say WHICH layer the current value came from. An operator who
  // cannot tell an inherited value from a pinned one re-pins defensively, and
  // that is how a fleet acquires an override nobody chose.
  const modelProvenance = loading
    ? 'Loading current model…'
    : currentModelExplicit
      ? `Currently ${modelLabel(currentModel)} — pinned for this fleet in fleet.yaml`
      : `Currently ${modelLabel(DEFAULT_MODEL)} — inherited (no fleet override)`
  const enabledChanged = !loading && enabled !== currentEnabled
  const hasChanges = !loading && !loadErr && (modelChanged || enabledChanged || role.trim() !== '')

  const propose = async () => {
    setBusy(true); setErr(null); setFailingChecks([])
    try {
      const r = await api.proposeAgent({
        profile: agent,
        mode: 'edit',
        model: modelChanged ? model : undefined,
        provider: modelChanged ? provider : undefined,
        enabled: enabledChanged ? enabled : undefined,
        role_description: role.trim() || undefined,
        message: note.trim() || undefined,
      })
      setResult(r)
      // Nothing is live yet (it's a PR pending review) — but re-baseline the
      // form against what was just proposed so "Propose changes" disables
      // again until the operator makes a further change.
      if (modelChanged) { setCurrentModel(model); setCurrentModelExplicit(true) }
      if (enabledChanged) setCurrentEnabled(enabled)
      setRole(''); setNote('')
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else setErr(em(e))
    } finally {
      setBusy(false)
    }
  }

  // ADR-184 D2: a core agent's MODEL is a fleet choice; everything else about it
  // is still core-owned. So this is no longer a dead end — it is a model-only
  // form, with the rest of the profile plainly marked as core's.
  //
  // This surface previously said "its profile can only be changed through core"
  // and offered nothing. That was true of the guard and false of the intent: 37
  // of 65 profiles across the active fleets are core-owned, including every
  // brain, and none of their models could be changed by anyone.
  if (isCoreOwned) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Profile</h1>
          <p className="text-sm text-gray-500 mt-1">
            {agent} is a <span className="font-medium">core-owned</span> agent (ADR-103). Its
            SOUL, skills, activities, routing and retirement are managed by core — but its{' '}
            <span className="font-medium">model</span> is this fleet's choice (ADR-184).
          </p>
        </div>

        {result && <ProposeResultBanner result={result} />}
        {catalogError && (
          <p className="text-xs text-red-600 whitespace-pre-wrap break-words">
            Couldn't load the model catalog ({catalogError}) — no model can be chosen
            until this loads.
          </p>
        )}
        {loadErr && (
          <p className="text-xs text-red-600 whitespace-pre-wrap break-words">
            Couldn't load {agent}'s current profile ({loadErr}) — "Propose model" is
            disabled until this loads, so a change is never proposed against a value
            that might not be current.
          </p>
        )}

        <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 px-4 py-3 space-y-3 max-w-xl">
          <label className="text-xs text-gray-600 block">Model
            <ModelPicker
              value={{ model, provider }}
              onChange={next => { setModel(next.model); setProvider(next.provider) }}
              catalog={catalog}
              loading={catalogLoading}
              disabled={loading}
              currentUnlisted={currentUnlisted}
            />
            <span className="block mt-1 text-[11px] text-gray-500">{modelProvenance}</span>
          </label>

          {err && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{err}</p>}
          {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}

          <div className="flex items-center gap-2">
            {canPropose ? (
              <button type="button" disabled={busy || !modelChanged} onClick={() => void propose()}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                {busy ? 'Proposing…' : 'Propose model'}
              </button>
            ) : <span className="text-xs text-gray-500">Only a fleet owner can propose a model change.</span>}
            <input type="text" value={note} onChange={e => setNote(e.target.value)}
              placeholder="Optional note for the reviewer" className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
          </div>
        </div>

        <p className="text-[11px] text-gray-500 max-w-xl">
          Enabled state, role, routing and retirement are not shown because they are
          core-owned for this agent — a proposal touching any of them is refused by
          the server, not just hidden here.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Profile</h1>
        <p className="text-sm text-gray-500 mt-1">
          {agent}'s model, enabled state, and role — writes to <span className="font-mono">config/fleet.yaml</span> via the structured agent editor (ADR-111 IA-4). Changes are proposed for in-app review; nothing runs until you approve it.
        </p>
      </div>

      {result && <ProposeResultBanner result={result} />}
      {catalogError && (
        <p className="text-xs text-red-600 whitespace-pre-wrap break-words">
          Couldn't load the model catalog ({catalogError}) — the Model list is empty
          until this loads, so nothing can be proposed against a model that might
          not exist.
        </p>
      )}
      {loadErr && (
        <p className="text-xs text-red-600 whitespace-pre-wrap break-words">
          Couldn't load {agent}'s current profile ({loadErr}) — showing platform defaults; "Propose changes" is disabled until this loads so a change is never proposed against a value that might not be current.
        </p>
      )}

      <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 px-4 py-3 space-y-3 max-w-xl">
        <div className="flex flex-wrap items-start gap-3">
          <label className="text-xs text-gray-600 block">Model
            <ModelPicker
              value={{ model, provider }}
              onChange={next => { setModel(next.model); setProvider(next.provider) }}
              catalog={catalog}
              loading={catalogLoading}
              disabled={loading}
              currentUnlisted={currentUnlisted}
            />
            <span className="block mt-1 text-[11px] text-gray-500">
              {loading
                ? 'Loading current model…'
                : currentModelExplicit
                  ? `Currently ${modelLabel(currentModel)} (set in fleet.yaml)`
                  : `Currently ${modelLabel(DEFAULT_MODEL)} (fleet default)`}
            </span>
          </label>
          <label className="text-xs text-gray-600 flex flex-col gap-1 pb-1.5">
            <span className="flex items-center gap-1.5">
              <input type="checkbox" checked={enabled} disabled={loading} onChange={e => setEnabled(e.target.checked)} />
              Agent enabled
            </span>
            <span className="text-[11px] text-gray-500 max-w-[16rem]">
              Whether this agent runs at all — independent of the model.
            </span>
          </label>
        </div>
        <label className="text-xs text-gray-600 block">Role description
          <input type="text" value={role} onChange={e => setRole(e.target.value)}
            placeholder="only applied if the profile already carries a role comment"
            className="mt-1 block w-full text-sm border border-gray-300 rounded px-2 py-1" />
        </label>

        {err && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{err}</p>}
        {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}

        <div className="flex items-center gap-2">
          {canPropose ? (
            <button type="button" disabled={busy || !hasChanges} onClick={() => void propose()}
              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
              {busy ? 'Proposing…' : 'Propose changes'}
            </button>
          ) : <span className="text-xs text-gray-500">Only a fleet owner can propose an agent change.</span>}
          <input type="text" value={note} onChange={e => setNote(e.target.value)}
            placeholder="Optional note for the reviewer" className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
        </div>
      </div>

      <RoutingSubForm agent={agent} canPropose={canPropose} />

      {/* ADR-123 — retire the agent. This is the widest-reaching delete in the
          Fleet Editor: it takes the agent's whole overlay (SOUL, skills,
          workflows, crons, op.env.tpl) plus its config/fleet.yaml profile block,
          regenerates the brain roster, and leaves Console/1Password/Slack work
          the operator must finish by hand. So it sits BELOW routing, visually
          separated, and every one of those consequences is enumerated in the
          plan before anything can be proposed. Delete is the only retirement
          action (D9) — there is no disable-instead affordance here. */}
      {canPropose && !isCoreOwned && (
        <div className="border-t-2 border-red-100 pt-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Retire this agent</p>
          <DeletePanel target={{ kind: 'agent', agent }} label={`Delete ${agent}`} />
        </div>
      )}
    </div>
  )
}
